import { getEnv } from "../utils/env";
import { getStorage } from "../platform";

export interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
}

interface CognitoConfig {
  userPoolId: string;
  clientId: string;
  region: string;
}

// Lazy so platform.configurePlatform() can run before the env adapter is
// consulted. Reading per-call is cheap (string property access).
function getConfig(): CognitoConfig {
  return {
    userPoolId: getEnv("VITE_COGNITO_USER_POOL_ID", ""),
    clientId: getEnv("VITE_COGNITO_CLIENT_ID", ""),
    region: getEnv("VITE_AWS_REGION", "us-east-1"),
  };
}

const STORAGE_KEYS = {
  accessToken: "slumbr-access-token",
  idToken: "slumbr-id-token",
  refreshToken: "slumbr-refresh-token",
} as const;

/**
 * Make a request to the Cognito User Pool API.
 *
 * Cognito uses a JSON-RPC style protocol over HTTPS:
 *  - Content-Type: application/x-amz-json-1.1
 *  - X-Amz-Target: AWSCognitoIdentityProviderService.<Action>
 */
async function cognitoRequest(
  action: string,
  payload: Record<string, unknown>
): Promise<Record<string, unknown>> {
  const endpoint = `https://cognito-idp.${getConfig().region}.amazonaws.com/`;

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": `AWSCognitoIdentityProviderService.${action}`,
    },
    body: JSON.stringify(payload),
  });

  const body = (await response.json()) as Record<string, unknown>;

  if (!response.ok) {
    const errorType =
      ((body.__type as string) || "").split("#").pop() || "CognitoError";
    const message = (body.message as string) || (body.Message as string) || "Authentication failed";
    const err = new Error(message);
    err.name = errorType;
    throw err;
  }

  return body;
}

/**
 * Register a new user with email and password.
 */
export async function signUp(
  email: string,
  password: string
): Promise<void> {
  const normalizedEmail = email.trim().toLowerCase();

  await cognitoRequest("SignUp", {
    ClientId: getConfig().clientId,
    Username: normalizedEmail,
    Password: password,
    UserAttributes: [{ Name: "email", Value: normalizedEmail }],
  });
}

/**
 * Confirm a user's sign-up with the verification code sent to their email.
 */
export async function confirmSignUp(
  email: string,
  code: string
): Promise<void> {
  await cognitoRequest("ConfirmSignUp", {
    ClientId: getConfig().clientId,
    Username: email.trim().toLowerCase(),
    ConfirmationCode: code.trim(),
  });
}

/**
 * Sign in with email and password. Returns tokens on success.
 */
export async function signIn(
  email: string,
  password: string
): Promise<AuthTokens> {
  const normalizedEmail = email.trim().toLowerCase();

  const result = await cognitoRequest("InitiateAuth", {
    ClientId: getConfig().clientId,
    AuthFlow: "USER_PASSWORD_AUTH",
    AuthParameters: {
      USERNAME: normalizedEmail,
      PASSWORD: password,
    },
  });

  const authResult = result.AuthenticationResult as {
    AccessToken: string;
    IdToken: string;
    RefreshToken: string;
  };

  if (!authResult) {
    throw new Error("Authentication challenge not supported");
  }

  const tokens: AuthTokens = {
    accessToken: authResult.AccessToken,
    idToken: authResult.IdToken,
    refreshToken: authResult.RefreshToken,
  };

  storeTokens(tokens);
  return tokens;
}

/**
 * Change the signed-in user's password. Uses the stored access token —
 * Cognito's ChangePassword verifies the current password server-side and
 * rotates to the new one. Throws with `.name` set to the Cognito error code
 * (e.g. NotAuthorizedException for a wrong current password,
 * InvalidPasswordException if the new one fails the pool policy).
 */
export async function changePassword(
  previousPassword: string,
  proposedPassword: string
): Promise<void> {
  const tokens = getStoredTokens();
  if (!tokens?.accessToken) {
    throw new Error("Not signed in");
  }
  await cognitoRequest("ChangePassword", {
    AccessToken: tokens.accessToken,
    PreviousPassword: previousPassword,
    ProposedPassword: proposedPassword,
  });
}

/**
 * Begin a password reset. Cognito emails the user a reset link/code (the email
 * copy is rendered by the CustomMessage Lambda trigger). Resolves with no value
 * — the pool has `preventUserExistenceErrors` on, so an unknown email looks the
 * same as a known one and we never leak which addresses are registered.
 */
export async function forgotPassword(email: string): Promise<void> {
  await cognitoRequest("ForgotPassword", {
    ClientId: getConfig().clientId,
    Username: email.trim().toLowerCase(),
  });
}

/**
 * Complete a password reset using the code from the reset email plus the new
 * password. Throws with `.name` set to the Cognito error code — e.g.
 * CodeMismatchException / ExpiredCodeException for a bad or stale code,
 * InvalidPasswordException if the new password fails the pool policy.
 */
export async function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string
): Promise<void> {
  await cognitoRequest("ConfirmForgotPassword", {
    ClientId: getConfig().clientId,
    Username: email.trim().toLowerCase(),
    ConfirmationCode: code.trim(),
    Password: newPassword,
  });
}

/**
 * Sign out — clears local tokens and calls Cognito GlobalSignOut
 * to invalidate the refresh token server-side.
 */
export async function signOut(): Promise<void> {
  const tokens = getStoredTokens();

  if (tokens) {
    try {
      await cognitoRequest("GlobalSignOut", {
        AccessToken: tokens.accessToken,
      });
    } catch {
      // Ignore errors — we're clearing locally regardless
    }
  }

  clearTokens();
}

/**
 * Refresh the access and id tokens using the stored refresh token.
 */
export async function refreshTokens(): Promise<AuthTokens> {
  const stored = getStoredTokens();
  if (!stored?.refreshToken) {
    throw new Error("No refresh token available");
  }

  const result = await cognitoRequest("InitiateAuth", {
    ClientId: getConfig().clientId,
    AuthFlow: "REFRESH_TOKEN_AUTH",
    AuthParameters: {
      REFRESH_TOKEN: stored.refreshToken,
    },
  });

  const authResult = result.AuthenticationResult as {
    AccessToken: string;
    IdToken: string;
  };

  if (!authResult) {
    throw new Error("Token refresh failed");
  }

  const tokens: AuthTokens = {
    accessToken: authResult.AccessToken,
    idToken: authResult.IdToken,
    // Cognito does not return a new refresh token on refresh
    refreshToken: stored.refreshToken,
  };

  storeTokens(tokens);
  return tokens;
}

/**
 * Read tokens from the configured platform storage.
 * Returns null when any token is missing or when storage isn't configured
 * (avoids throwing during early renders / SSR / tests).
 */
export function getStoredTokens(): AuthTokens | null {
  let storage;
  try {
    storage = getStorage();
  } catch {
    return null;
  }

  const accessToken = storage.getItem(STORAGE_KEYS.accessToken);
  const idToken = storage.getItem(STORAGE_KEYS.idToken);
  const refreshToken = storage.getItem(STORAGE_KEYS.refreshToken);

  if (!accessToken || !idToken || !refreshToken) return null;

  return { accessToken, idToken, refreshToken };
}

/**
 * Persist tokens to the configured platform storage.
 */
export function storeTokens(tokens: AuthTokens): void {
  let storage;
  try {
    storage = getStorage();
  } catch {
    return;
  }
  storage.setItem(STORAGE_KEYS.accessToken, tokens.accessToken);
  storage.setItem(STORAGE_KEYS.idToken, tokens.idToken);
  storage.setItem(STORAGE_KEYS.refreshToken, tokens.refreshToken);
}

/**
 * Remove all stored tokens.
 */
export function clearTokens(): void {
  let storage;
  try {
    storage = getStorage();
  } catch {
    return;
  }
  storage.removeItem(STORAGE_KEYS.accessToken);
  storage.removeItem(STORAGE_KEYS.idToken);
  storage.removeItem(STORAGE_KEYS.refreshToken);
  // The persona choice belongs to the signed-out account: left behind, the NEXT account to sign in on this device
  // sends the previous user's profile id as X-Slumbr-Profile (and the login flow skips adopting its own persona).
  storage.removeItem("slumbr-profile-switcher");
}

/**
 * Check if the user has stored tokens and the access token is not expired.
 * Uses a simple JWT expiry check without verifying the signature.
 */
export function isAuthenticated(): boolean {
  const tokens = getStoredTokens();
  if (!tokens) return false;

  try {
    const payload = JSON.parse(atob(tokens.accessToken.split(".")[1])) as {
      exp: number;
    };
    // Treat as expired if within 30 seconds of expiry
    return payload.exp * 1000 > Date.now() + 30_000;
  } catch {
    return false;
  }
}

/** Refresh if the token expires within this window (covers clock skew + the
 *  round trip), so requests never go out with an about-to-die token. */
const EXPIRY_SKEW_MS = 60_000;

/** Cognito error names that mean the session is genuinely dead — the user must
 *  sign in again. Everything else (network, 5xx, throttling) is transient and
 *  must NOT end the session. Keep in sync with the app-level refresh hooks. */
const FATAL_AUTH_ERRORS = new Set([
  "NotAuthorizedException",
  "InvalidRefreshTokenException",
  "TokenExpiredException",
  "UserNotFoundException",
  "UserNotConfirmedException",
]);

export function isFatalAuthError(err: unknown): boolean {
  return err instanceof Error && FATAL_AUTH_ERRORS.has(err.name);
}

/** Decode a JWT's `exp` claim (ms epoch) without verifying the signature.
 *  Returns null when the token can't be parsed. */
function tokenExpMs(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1])) as { exp?: number };
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

let inFlightRefresh: Promise<AuthTokens> | null = null;

/**
 * Refresh the session, coalescing concurrent callers into a single network
 * round trip. Without this, a burst of requests that all find an expired token
 * would each fire their own REFRESH_TOKEN_AUTH call and race to write tokens.
 */
export function refreshSession(): Promise<AuthTokens> {
  if (!inFlightRefresh) {
    inFlightRefresh = refreshTokens().finally(() => {
      inFlightRefresh = null;
    });
  }
  return inFlightRefresh;
}

/**
 * Return a non-expired idToken for an outgoing API request, refreshing first
 * when the current one is expired or about to expire. Returns null only when
 * there's no session. A transient refresh failure falls back to the existing
 * token (the request's own 401 handling will retry); a fatal failure clears
 * the tokens so the app can route to login.
 */
export async function getValidIdToken(): Promise<string | null> {
  const tokens = getStoredTokens();
  if (!tokens) return null;

  const expMs = tokenExpMs(tokens.idToken);
  const needsRefresh = expMs !== null && expMs <= Date.now() + EXPIRY_SKEW_MS;
  if (!needsRefresh) return tokens.idToken;

  try {
    return (await refreshSession()).idToken;
  } catch (err) {
    if (isFatalAuthError(err)) {
      clearTokens();
      return null;
    }
    return tokens.idToken;
  }
}

/**
 * Recover from a 401 by minting a fresh idToken. Resolves with the new token
 * (retry the request), resolves with null when the session is genuinely dead
 * (tokens cleared → caller should log out), or rejects on a transient failure
 * (caller fails the one request but keeps the user signed in).
 */
export async function forceRefreshIdToken(): Promise<string | null> {
  if (!getStoredTokens()) return null;
  try {
    return (await refreshSession()).idToken;
  } catch (err) {
    if (isFatalAuthError(err)) {
      clearTokens();
      return null;
    }
    throw err;
  }
}
